import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MessageSquare, Plus, Loader2, Trash2, Save, X } from "lucide-react";

const PRICE_OPTIONS = [
  { value: "", label: "—" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function ChipList({ values, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(values || []), v]);
    setDraft("");
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {(values || []).map((v, i) => (
          <span key={i} className="pill pill-neutral inline-flex items-center gap-1.5" data-testid={`${testid}-${i}`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-meta hover:text-danger"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder} data-testid={`${testid}-input`}
          className="flex-1 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs outline-none focus:border-ai" />
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="rounded-full bg-ai-soft text-ai px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1">
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function SegmentCard({ seg, onSave, onDelete }) {
  const [draft, setDraft] = useState(seg);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(seg);
  useEffect(() => { setDraft(seg); }, [seg]);

  const save = async () => {
    setBusy(true);
    try { await onSave(draft); } finally { setBusy(false); }
  };

  const set = (k, v) => setDraft({ ...draft, [k]: v });

  return (
    <div className="surface p-5 space-y-4" data-testid={`segment-${seg.id}`}>
      <div className="flex items-start justify-between gap-3">
        <input value={draft.name || ""} onChange={(e) => set("name", e.target.value)} data-testid={`segment-name-${seg.id}`}
          className="flex-1 min-w-0 font-display text-lg font-bold text-ink bg-transparent outline-none border-b border-transparent focus:border-ai" />
        <button onClick={() => onDelete(seg.id)} className="text-meta hover:text-danger p-1" data-testid={`segment-delete-${seg.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <Field label="Persona" multiline value={draft.persona} onChange={(v) => set("persona", v)} testid={`segment-persona-${seg.id}`}
        placeholder="Solo creator, 25-35, $5-20k/mo, stuck reactive" />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Pain points</Label>
          <ChipList values={draft.pain_points} onChange={(v) => set("pain_points", v)} placeholder="Reactive DMs" testid={`segment-pain-${seg.id}`} />
        </div>
        <div>
          <Label>Desires</Label>
          <ChipList values={draft.desires} onChange={(v) => set("desires", v)} placeholder="Predictable revenue" testid={`segment-desire-${seg.id}`} />
        </div>
        <div>
          <Label>Objections</Label>
          <ChipList values={draft.objections} onChange={(v) => set("objections", v)} placeholder="Tried tools before" testid={`segment-objection-${seg.id}`} />
        </div>
        <div>
          <Label>Buying signals</Label>
          <ChipList values={draft.buying_signals} onChange={(v) => set("buying_signals", v)} placeholder="Asks about ROI" testid={`segment-signal-${seg.id}`} />
        </div>
        <div>
          <Label>Channels</Label>
          <ChipList values={draft.channels} onChange={(v) => set("channels", v)} placeholder="Instagram" testid={`segment-channel-${seg.id}`} />
        </div>
        <div>
          <Label>Price sensitivity</Label>
          <select value={draft.price_sensitivity || ""} onChange={(e) => set("price_sensitivity", e.target.value || null)} data-testid={`segment-price-${seg.id}`}
            className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai">
            {PRICE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <Field label="Notes" multiline value={draft.notes} onChange={(v) => set("notes", v)} testid={`segment-notes-${seg.id}`}
        placeholder="Anything else your Workforce should know" />

      {dirty && (
        <div className="flex justify-end">
          <button onClick={save} disabled={busy} data-testid={`segment-save-${seg.id}`}
            className="rounded-full ashra-gradient-bg px-4 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save changes
          </button>
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <p className="text-[11px] uppercase tracking-wider text-meta font-bold mb-1.5">{children}</p>;
}

function Field({ label, value, onChange, multiline, placeholder, testid }) {
  return (
    <label className="block space-y-1.5">
      <Label>{label}</Label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={2} data-testid={testid}
          className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition resize-none placeholder:text-meta-soft" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition placeholder:text-meta-soft" />
      )}
    </label>
  );
}

export default function CustomerMemory() {
  const [segments, setSegments] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/nexus/customer");
      setSegments(r.data || []);
    } catch {
      setSegments([]);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/nexus/customer", { name: "New segment" });
      await load();
      toast.success("Segment created");
    } catch {
      toast.error("Could not create segment");
    }
    setCreating(false);
  };

  const save = async (draft) => {
    try {
      await api.patch(`/nexus/customer/${draft.id}`, draft);
      toast.success("Segment saved · grounding outputs");
      await load();
    } catch {
      toast.error("Could not save segment");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this segment?")) return;
    try {
      await api.delete(`/nexus/customer/${id}`);
      await load();
    } catch {}
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px] mx-auto space-y-5" data-testid="nexus-customer-page">
      <div className="surface p-5 flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-ai-soft text-ai grid place-items-center"><MessageSquare className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-bold text-ink">Customer Memory</h2>
          <p className="text-xs text-meta">Persona, pain, desire, objection — segment by segment. Forge picks the most-fit segment automatically.</p>
        </div>
        <button onClick={create} disabled={creating} data-testid="segment-create"
          className="rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0">
          {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} New segment
        </button>
      </div>

      {segments === null ? (
        <div className="surface p-10 text-center text-meta inline-flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : segments.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="segment-empty">
          <p className="text-sm text-meta">No segments yet. Add one to ground every generation in real customer context.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {segments.map(s => <SegmentCard key={s.id} seg={s} onSave={save} onDelete={del} />)}
        </div>
      )}
    </div>
  );
}
